import { useState } from 'react';
import { api } from '../api';
import { EmailForm } from './EmailForm';

/**
 * Share the week — as a PDF, a spreadsheet, or an email.
 *
 * What gets shared is NOT this page. The dashboard is built to be read at a
 * desk, with a coverage panel, drill-downs and a refresh control; the shared
 * version is one screen — six figures, the desks under them, the caveats, and a
 * line saying where the numbers came from. That condensing happens on the
 * server (exports/dashboardShare.ts) so the email, the PDF and the spreadsheet
 * cannot drift apart.
 *
 * Design notes worth keeping:
 *
 * - Collapsed by default. This sits above the figures, and a permanently open
 *   email form would push the numbers below the fold on a laptop for the sake
 *   of something used once a week.
 * - Preview before send. "Email friendly" is a claim nobody can check from a
 *   form, and a director who has seen the thing will actually press send.
 * - The attachments are optional and OFF by default. The figures are in the
 *   body of the email; that is the whole point of the condensed version, and it
 *   means a phone reader gets the summary without opening anything.
 */
export function ShareSummary({ week }: { week: string | null }) {
  const [open, setOpen] = useState(false);
  // Committed separately from the live note so the preview does not reload on
  // every keystroke — an iframe refetching a PDF-shaped page per character is
  // both ugly and a lot of pointless renders.
  const [previewNote, setPreviewNote] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const query = (extra?: Record<string, string>) => {
    const params = new URLSearchParams();
    if (week) params.set('week', week);
    for (const [k, v] of Object.entries(extra ?? {})) if (v) params.set(k, v);
    const s = params.toString();
    return s ? `?${s}` : '';
  };

  return (
    <div className="card" style={{ marginBottom: 14, padding: '12px 16px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 200 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>Share this week</h3>
          <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--muted)' }}>
            A one-page version of the figures below — headlines and desks, without the workings.
          </p>
        </div>

        {/* Plain anchors, like the report exports. The browser handles the
            download and the file name comes from the server, so there is no
            blob to build or revoke here. */}
        <a className="btn" href={`/api/dashboard/export.pdf${query()}`}>
          Download PDF
        </a>
        <a className="btn" href={`/api/dashboard/export.xlsx${query()}`}>
          Download Excel
        </a>
        <button className="btn primary" onClick={() => setOpen((v) => !v)}>
          {open ? 'Close' : 'Email it'}
        </button>
      </div>

      {open ? (
        <div style={{ marginTop: 14, borderTop: '1px solid var(--border, #ece7f2)', paddingTop: 14 }}>
          <EmailForm
            onSend={async ({ to, note, formats }) => {
              const res = await api.post<{ detail: string }>('/api/dashboard/share', { to, note, week, formats });
              return res.detail;
            }}
            notePlaceholder="Ageing vacancies are the one to look at this week."
            onNoteCommit={setPreviewNote}
            extraActions={
              <button className="btn btn-quiet" onClick={() => setShowPreview((v) => !v)}>
                {showPreview ? 'Hide preview' : 'Preview'}
              </button>
            }
            explain={
              <>
                The figures are in the body of the email, so it reads on a phone without opening anything. Named
                individuals are not in the email — they are on the third tab of the spreadsheet.
              </>
            }
          />

          {showPreview ? (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 6 }}>
                Exactly what recipients get, and what the PDF contains.
              </div>
              <iframe
                title="Summary preview"
                // Keyed on the inputs so changing the week or committing a note
                // refetches, rather than showing a stale render.
                key={`${week}|${previewNote}`}
                src={`/api/dashboard/preview${query({ note: previewNote })}`}
                style={{
                  width: '100%',
                  height: 620,
                  border: '1px solid var(--border, #ece7f2)',
                  borderRadius: 6,
                  background: '#fff',
                }}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
